import {
  faCalendarDays,
  faCrosshairsSimple,
  faEnvelope,
  faListCheck,
  faMessage,
  faPaperPlaneTop,
  faStickyNote
} from '@fortawesome/pro-solid-svg-icons'
import type { ReactElement } from 'react'
import { useContext, useState } from 'react'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonIcon } from '~/components/Global/Interactions/Button/ButtonIcon'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { EmailAction } from '~/components/Scopes/Actions/EmailAction'
import { MeetingAction } from '~/components/Scopes/Actions/MeetingAction'
import { MessageAction } from '~/components/Scopes/Actions/MessageAction'
import { NoteAction } from '~/components/Scopes/Actions/NoteAction'
import { useConfig } from '~/domains/configuration/queries/config'
import { CreateOrderModal } from '~/domains/orders/components/CreateOrderModal'
import { TaskAction } from '~/domains/tasks/components/TaskAction/TaskAction'
import { AsideActionContext } from '~/utils/contextes'

type ContactsBulkActionsProps = {
  contactIds: number[]
  onRemoveContact: (contactId: number) => void
}

const limitedLabel = (label: string, limit: number | undefined) =>
  limit === undefined
    ? label
    : `${label} (limité à ${limit} contact${limit > 1 ? 's' : ''})`

export const ContactsBulkActions = ({
  contactIds,
  onRemoveContact
}: ContactsBulkActionsProps): ReactElement => {
  const { setAsideAction } = useContext(AsideActionContext)
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data: smsLimit } = useConfig('actions_limit.sms')
  const { data: emailLimit } = useConfig('actions_limit.email')
  const { data: recallLimit } = useConfig('actions_limit.contact_recall')
  const { data: meetingLimit } = useConfig('actions_limit.contact_meeting')
  const [createRecourses, setCreateRecourses] = useState(false)

  const smsLimitReached = smsLimit !== undefined && contactIds.length > smsLimit
  const emailLimitReached =
    emailLimit !== undefined && contactIds.length > emailLimit
  const recallLimitReached =
    recallLimit !== undefined && contactIds.length > recallLimit
  const meetingLimitReached =
    meetingLimit !== undefined && contactIds.length > meetingLimit

  const actions = [
    {
      label: 'Placement actif',
      icon: faPaperPlaneTop,
      onClick: () => {
        window.open(
          `${legacyUrl}/admin/push/?contacts=${contactIds.join(',')}`,
          '_blank'
        )
      }
    },
    {
      label: smsLimitReached
        ? limitedLabel('Envoyer un SMS', smsLimit)
        : 'Envoyer un SMS',
      icon: faMessage,
      disabled: smsLimitReached,
      onClick: () => {
        setAsideAction({
          action: (
            <MessageAction
              contactIds={contactIds}
              removeRecipientIdFromList={onRemoveContact}
            />
          )
        })
      }
    },
    {
      label: emailLimitReached
        ? limitedLabel('Envoyer un email', emailLimit)
        : 'Envoyer un email',
      icon: faEnvelope,
      disabled: emailLimitReached,
      onClick: () => {
        setAsideAction({
          action: <EmailAction contactsIds={contactIds} sendTo="contact" />
        })
      }
    },
    {
      label: recallLimitReached
        ? limitedLabel('Ajouter une tâche', recallLimit)
        : 'Ajouter une tâche',
      icon: faListCheck,
      disabled: recallLimitReached,
      onClick: () => {
        setAsideAction({ action: <TaskAction contactIds={contactIds} /> })
      }
    },
    {
      label: meetingLimitReached
        ? limitedLabel('Planifier un rendez-vous', meetingLimit)
        : 'Planifier un rendez-vous',
      icon: faCalendarDays,
      disabled: meetingLimitReached,
      onClick: () => {
        setAsideAction({ action: <MeetingAction contactIds={contactIds} /> })
      }
    },
    {
      label: 'Créer une note',
      icon: faStickyNote,
      onClick: () => {
        setAsideAction({ action: <NoteAction contactIds={contactIds} /> })
      }
    },
    {
      label: 'Création groupée de recours',
      icon: faCrosshairsSimple,
      onClick: () => setCreateRecourses(true)
    }
  ]

  return (
    <>
      {actions.map((action) => (
        <Tooltip key={action.icon.iconName} text={action.label} direction="top">
          <Button
            variation={ButtonVariation.PrimaryInversed}
            onClick={action.onClick}
            disabled={action.disabled}
            narrow
          >
            <ButtonIcon icon={action.icon} />
          </Button>
        </Tooltip>
      ))}

      {createRecourses && (
        <CreateOrderModal
          contactIds={contactIds}
          resourceType="recourse"
          open
          onClose={() => setCreateRecourses(false)}
        />
      )}
    </>
  )
}
